import { useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Star, X } from 'lucide-react';
import { normalisePositions, samePositions, type Position, type PositionValue } from '@tradeoutlet/shared';
import { Button, ConfirmDialog } from '@/components/ui';
import { positionTone } from '@/lib/color';

// t331u: shared with the phone pages.
export { normalisePositions, samePositions, type PositionValue } from '@tradeoutlet/shared';

/**
 * Feedback #4: searchable multi-select for positions. Chips with a confirmed remove, a clear primary,
 * and nothing is saved until Save. `mode="bulk"` drops the primary (it's per person).
 */
export function PositionPicker({ positions, value, onSave, mode = 'edit', personName, saveLabel = 'Save positions', onCancel }: {
  positions: Position[]; value: PositionValue; onSave: (v: PositionValue) => Promise<unknown> | void; mode?: 'edit' | 'bulk';
  personName?: string; saveLabel?: string; onCancel?: () => void;
}) {
  const initial = useMemo(() => (mode === 'edit' ? normalisePositions(value) : { ids: value.ids, primary: null }), [value, mode]);
  const [draft, setDraft] = useState<PositionValue>(initial);
  useEffect(() => setDraft(initial), [initial]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const uid = useId();
  const byId = new Map(positions.map(p => [p.id, p]));
  const options = positions.filter(p => !p.archived_at && !draft.ids.includes(p.id) && p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const dirty = !samePositions(draft, initial);
  const set = (v: PositionValue) => setDraft(mode === 'edit' ? normalisePositions(v) : { ids: v.ids, primary: null });
  const add = (id: string) => { set({ ...draft, ids: [...draft.ids, id] }); setQ(''); setActive(0); input.current?.focus(); };
  const remove = (id: string) => set({ ...draft, ids: draft.ids.filter(x => x !== id) });
  const save = async () => { setSaving(true); try { await onSave(draft); } finally { setSaving(false); } };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(i => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && options[active]) add(options[active].id); }
    else if (e.key === 'Escape') { setOpen(false); }
    else if (e.key === 'Backspace' && !q && draft.ids.length) setConfirm(draft.ids[draft.ids.length - 1]);
  };
  const name = (id: string) => byId.get(id)?.name ?? 'Unknown position';
  return (
    <div className="space-y-3" data-testid="position-picker">
      <div className="flex flex-wrap gap-2" aria-label="Selected positions" role="list">
        {draft.ids.length === 0 && <span className="text-sm text-slate-500">{mode === 'bulk' ? 'Pick positions to apply.' : 'No positions yet.'}</span>}
        {draft.ids.map(id => {
          const p = byId.get(id); const tone = positionTone(p?.color ?? '#94a3b8'); const primary = draft.primary === id;
          return (
            <span key={id} role="listitem" className={clsx('inline-flex items-center gap-1 rounded-full border py-0.5 pl-2 pr-1 text-sm', primary && 'ring-2 ring-slate-900 dark:ring-white')}
              style={{ background: tone.bg, borderColor: tone.border, color: tone.text }} data-testid={`chip-${id}`}>
              {mode === 'edit' && (
                <button type="button" aria-pressed={primary} aria-label={primary ? `${name(id)} is the primary position` : `Make ${name(id)} the primary position`}
                  title={primary ? 'Primary position' : 'Make primary'} onClick={() => set({ ...draft, primary: id })} className="rounded-full p-0.5 hover:bg-black/10">
                  <Star className={clsx('h-3.5 w-3.5', primary ? 'fill-current' : 'opacity-50')} />
                </button>
              )}
              <span>{name(id)}</span>{primary && <span className="text-[10px] font-semibold uppercase tracking-wide">Primary</span>}
              <button type="button" aria-label={`Remove ${name(id)}`} onClick={() => setConfirm(id)} className="rounded-full p-0.5 hover:bg-black/10"><X className="h-3.5 w-3.5" /></button>
            </span>
          );
        })}
      </div>
      <div className="relative max-w-sm">
        <input ref={input} role="combobox" aria-expanded={open} aria-controls={`${uid}-list`} aria-autocomplete="list" aria-label="Add a position"
          aria-activedescendant={open && options[active] ? `${uid}-${options[active].id}` : undefined}
          className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900" placeholder="Search positions to add…"
          value={q} onChange={e => { setQ(e.target.value); setOpen(true); setActive(0); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)} onKeyDown={onKey} />
        {open && (
          <ul id={`${uid}-list`} role="listbox" className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900">
            {options.length === 0 && <li className="px-3 py-2 text-slate-500" role="presentation">{q ? `No positions match “${q}”` : 'All positions added'}</li>}
            {options.map((p, i) => (
              <li key={p.id} id={`${uid}-${p.id}`} role="option" aria-selected={i === active} onMouseDown={e => { e.preventDefault(); add(p.id); }} onMouseEnter={() => setActive(i)}
                className={clsx('flex cursor-pointer items-center gap-2 px-3 py-1.5', i === active && 'bg-slate-100 dark:bg-slate-800')}>
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: positionTone(p.color).border }} />{p.name}
              </li>
            ))}
          </ul>
        )}
      </div>
      {(dirty || onCancel) && (
        <div className="flex justify-end gap-2" data-testid="position-actions">
          <Button variant="ghost" onClick={() => { setDraft(initial); onCancel?.(); }} disabled={saving}>Cancel</Button>
          <Button onClick={() => void save()} loading={saving} disabled={!dirty && mode === 'edit'}>{saveLabel}</Button>
        </div>
      )}
      {confirm && (
        <ConfirmDialog open title={`Remove ${name(confirm)}?`} confirmLabel="Remove" onCancel={() => setConfirm(null)} onConfirm={() => { remove(confirm); setConfirm(null); }}>
          {mode === 'bulk' ? 'It won’t be applied.' : `${personName ?? 'They'} won’t be offered ${name(confirm)} shifts once you save.`}{draft.primary === confirm && mode === 'edit' ? ' It’s the primary position, so the next one becomes primary.' : ''}
        </ConfirmDialog>
      )}
    </div>
  );
}
